import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Search, Download, ShoppingBag, CheckCircle, Clock, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

export function AdminUsersTab({
  userAnalytics, searchTerm, setSearchTerm, sortField, sortDir,
  toggleSort, sortData, expandedUser, setExpandedUser,
  setSelectedUser, setShowRoleDialog, setShowDeleteDialog, exportUsers
}) {
  const filteredUsers = userAnalytics.filter(u =>
    !searchTerm || u.name?.toLowerCase().includes(searchTerm.toLowerCase()) || u.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown className="h-3 w-3 opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />;
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-3">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
          <Input placeholder="Buscar usuario..." value={searchTerm} onChange={e => setSearchTerm(e.target.value)}
            className="pl-10" data-testid="user-search" />
        </div>
        <span className="text-sm text-slate-500">{filteredUsers.length} usuarios</span>
        <Button size="sm" variant="outline" className="border-slate-300 text-xs ml-auto" onClick={exportUsers} data-testid="export-users-btn">
          <Download className="h-3.5 w-3.5 mr-1" /> CSV
        </Button>
      </div>

      <div className="flex gap-2 flex-wrap text-xs">
        {[
          { field: 'name', label: 'Nome' },
          { field: 'total_claims', label: 'Resgates' },
          { field: 'total_validations', label: 'Validacoes' },
          { field: 'gamification_points', label: 'Pontos' },
          { field: 'created_at', label: 'Data' }
        ].map(s => (
          <button key={s.field} onClick={() => toggleSort(s.field)}
            className={`flex items-center gap-1 px-2 py-1 rounded border ${sortField === s.field ? 'bg-slate-100 border-slate-300 font-semibold' : 'border-slate-200 text-slate-500'}`}>
            {s.label} <SortIcon field={s.field} />
          </button>
        ))}
      </div>

      <div className="space-y-2">
        {sortData(filteredUsers, sortField, sortDir).map(u => (
          <Card key={u.user_id} className="bg-white border border-slate-200" data-testid={`admin-user-${u.user_id}`}>
            <CardContent className="py-3">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div className="flex items-center gap-3 cursor-pointer" onClick={() => setExpandedUser(expandedUser === u.user_id ? null : u.user_id)}>
                  {u.picture ? (
                    <img src={u.picture} alt="" className="w-10 h-10 rounded-full" />
                  ) : (
                    <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                      <span className="text-slate-500 font-bold">{(u.name || 'U')[0]}</span>
                    </div>
                  )}
                  <div>
                    <p className="font-semibold text-slate-900">{u.name}</p>
                    <p className="text-xs text-slate-500">{u.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge className="bg-blue-50 text-blue-700 border-blue-200 text-xs">
                    <ShoppingBag className="h-3 w-3 mr-1" />{u.total_claims} resgates
                  </Badge>
                  <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 text-xs">
                    <CheckCircle className="h-3 w-3 mr-1" />{u.total_validations} validados
                  </Badge>
                  <Badge className="bg-amber-50 text-amber-700 border-amber-200 text-xs">
                    <Clock className="h-3 w-3 mr-1" />{u.pending_claims} pendentes
                  </Badge>
                  <Badge className="bg-slate-100 text-slate-600 border-slate-200 text-xs">
                    {u.gamification_points} pts
                  </Badge>
                  <Button size="sm" variant="outline" className="text-xs border-slate-300 h-7"
                    onClick={() => { setSelectedUser({ ...u, role: 'user' }); setShowRoleDialog(true); }}
                    data-testid={`edit-role-${u.user_id}`}>
                    Role
                  </Button>
                  <Button size="sm" variant="ghost" className="text-xs text-red-500 hover:text-red-700 h-7"
                    onClick={() => { setSelectedUser(u); setShowDeleteDialog(true); }}
                    data-testid={`delete-user-${u.user_id}`}>
                    <Trash2 className="h-3.5 w-3.5" />
                  </Button>
                </div>
              </div>
              {expandedUser === u.user_id && (
                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                  <div><p className="text-slate-400 text-xs">Cidade</p><p className="text-slate-700">{u.city || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Bairro</p><p className="text-slate-700">{u.neighborhood || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">WhatsApp</p><p className="text-slate-700">{u.whatsapp || '-'}</p></div>
                  <div><p className="text-slate-400 text-xs">Cadastro</p><p className="text-slate-700">{new Date(u.created_at).toLocaleDateString('pt-BR')}</p></div>
                  <div className="col-span-2 md:col-span-4">
                    <p className="text-slate-400 text-xs mb-1">Categorias Acessadas</p>
                    <div className="flex gap-1 flex-wrap">
                      {u.categories_accessed?.length > 0 ? u.categories_accessed.map(cat => (
                        <Badge key={cat} className="bg-slate-100 text-slate-600 border-slate-200 text-xs">{cat}</Badge>
                      )) : <span className="text-xs text-slate-400">Nenhuma</span>}
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
